import type { IconName } from '@eliadmin/core/src/components/nt/ui/icon';
import { searchText } from '@eliadmin/core/src/components/nt/account/orders/orders-data';

/** Where an order is in the design's terms. */
export type OrderStage = 'received' | 'sent' | 'delivered' | 'cancelled';

/**
 * The list filters of Figma „Filter“ (207:11671), in the order of the admin list „Konto - filtre
 * objednávok“ („Všetky | Aktívne | Doručené | Zrušené“).
 */
export const ORDER_FILTERS = ['all', 'active', 'delivered', 'cancelled'] as const;
export type OrderFilter = (typeof ORDER_FILTERS)[number];

/**
 * The shop's order statuses mapped onto the design by words of the status NAME (lower case,
 * without diacritics, at the start of a word; the first rule wins). A name matching none is an
 * order received and in progress (Prijatá, Nová, Pripravujeme, Čaká na platbu …).
 *
 *   cancelled  Zrušená, Stornovaná
 *   delivered  Doručená, Vybavená, Dokončená, Uzavretá
 *   sent       Odoslaná, Expedovaná, Na ceste, Pripravená na odber / na vyzdvihnutie
 */
const STAGE_RULES: { stage: Exclude<OrderStage, 'received'>; words: RegExp }[] = [
  { stage: 'cancelled', words: /\b(zrus|storn)/ },
  { stage: 'delivered', words: /\b(doruc|vybaven|dokoncen|uzavret)/ },
  { stage: 'sent', words: /\b(odoslan|expedovan|na ceste|pripravena na (odber|vyzdvihnutie))/ },
];

export function orderStage(status: { name?: string | null } | null | undefined): OrderStage {
  const name = searchText(status?.name ?? '');
  return STAGE_RULES.find(rule => rule.words.test(name))?.stage ?? 'received';
}

/** The filter an order is counted in besides „Všetky“. */
export function stageFilter(stage: OrderStage): Exclude<OrderFilter, 'all'> {
  return stage === 'received' || stage === 'sent' ? 'active' : stage;
}

/** Figma „Status“ pill icons: clock in progress, check-circle delivered, x-circle cancelled. */
export const STAGE_ICON: Record<OrderStage, IconName> = {
  received: 'clock',
  sent: 'truck',
  delivered: 'check-circle',
  cancelled: 'x-circle',
};

/**
 * The step of „Stav objednávky“ (admin list „Objednávka - stavy“: „Prijatá | Pripravujeme |
 * Odoslaná | Doručená“) the order is at: the step named like the status, otherwise by its stage –
 * received → the first, sent → the one before the last, delivered → the last. `-1` = cancelled.
 */
export function orderStep(status: { name?: string | null }, steps: string[]) {
  const stage = orderStage(status);
  if (stage === 'cancelled' || !steps.length) return -1;
  const name = searchText(status.name ?? '');
  const named = steps.findIndex(step => searchText(step) === name);
  if (named >= 0) return named;
  if (stage === 'delivered') return steps.length - 1;
  if (stage === 'sent') return Math.max(0, steps.length - 2);
  return 0;
}
